import { useContext } from 'react';

import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { IContextMenuItem } from '@smartshell/ui-kit';
import { useNavigate } from 'react-router';

import { LocalizationContext } from 'localization';
import {
  DELETE_PROMO_CAMPAIGN,
  IDeletePromoCampaignRequest,
  IDeletePromoCampaignResponse,
  IPromoCampaignResponse,
  PROMO_CAMPAIGN_TO_CSV,
  TPromoCampaignData,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';

import s from './ContextMenuItems.module.scss';

export const useContextMenuItemsCampaign = (
  refetchCampaign: (
    variables?: Partial<OperationVariables>
  ) => Promise<ApolloQueryResult<IPromoCampaignResponse>>,
  onGenerateClick: (id: string) => void
): Array<IContextMenuItem<TPromoCampaignData>> => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();

  const [exportCampaignToCsv] = useLazyQuery(PROMO_CAMPAIGN_TO_CSV, {
    fetchPolicy: 'cache-and-network',
  });

  const [deleteCampaign] = useMutation<
    IDeletePromoCampaignResponse,
    IDeletePromoCampaignRequest
  >(DELETE_PROMO_CAMPAIGN, {
    onCompleted: (res) => {
      if (!res.deletePromoCampaign) return;
      refetchCampaign();
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  return [
    {
      key: 'export',
      text: t('export.scv'),
      iconName: 'IncomeIcon',
      onClick: (promoCode: TPromoCampaignData) =>
        exportCampaignToCsv({
          variables: { promoCampaignCodesToCsvId: promoCode.id },
        }).then((res) => {
          window.open(res.data.promoCampaignCodesToCsv);
        }),
    },
    {
      key: 'duplicate',
      text: t('promo.campaign.duplicate'),
      iconName: 'CopyIcon',
      onClick: (promoCode: TPromoCampaignData) =>
        navigate(`/promo-campaign/${promoCode.id}?action=duplicate`),
    },
    {
      key: 'generate',
      text: t('promo.generate.add'),
      iconName: 'CirclePlusIcon',
      onClick: (promoCode: TPromoCampaignData) => onGenerateClick(promoCode.id),
    },
    {
      key: 'edit',
      text: t('edit'),
      iconName: 'EditIcon',
      onClick: (promoCode: TPromoCampaignData) =>
        navigate(`/promo-campaign/${promoCode.id}`),
    },
    {
      key: 'delete',
      className: s['remove-item'],
      text: t('delete'),
      iconName: 'BucketIcon',
      onClick: (promoCode: TPromoCampaignData) => {
        openConfirmDialog(t, 'remove', {
          title: t('delete.promo_code.group'),
          message: t('promo.campaign.delete.text', promoCode.title),
          acceptTitle: t('delete'),
          declineTitle: t('cancel'),
        }).then(({ status }) => {
          if (status === 'ACCEPTED') {
            deleteCampaign({ variables: { id: promoCode.id } });
          }
        });
      },
    },
  ];
};
